import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EyeIcon } from "lucide-react";
import { formatDateLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import type { ReportListItem } from "../api/client";

const STATUS_OPTIONS: Record<
  string,
  {
    label: string;
    variant: "success" | "destructive" | "secondary" | "default";
  }
> = {
  open: { label: "ເປີດ", variant: "default" },
  reviewing: { label: "ກຳລັງກວດສອບ", variant: "secondary" },
  actioned: { label: "ດຳເນີນການແລ້ວ", variant: "success" },
  dismissed: { label: "ປິດລາຍງານ", variant: "secondary" },
};

const TARGET_TYPE_LABELS: Record<string, string> = {
  listing: "ລາຍການສິນຄ້າ",
  review: "ຄຳແນະນຳ",
  shop: "ຮ້ານຄ້າ",
  mechanic: "ຊ່າງແປງ",
  job: "ວຽກ",
};

type ReportsTableProps = {
  isLoading: boolean;
  data: ReportListItem[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onView: (row: ReportListItem) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

export function ReportsTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onView,
  onPaginationChange,
  onSortingChange,
}: ReportsTableProps) {
  const columns: TanstackReactTable.ColumnDef<ReportListItem>[] = [
    createSortableColumn<ReportListItem>("targetType", "ປະເພດເປົ້າໝາຍ", {
      size: 120,
      cell: ({ row }) =>
        TARGET_TYPE_LABELS[row.original.targetType] ?? row.original.targetType,
    }),
    {
      id: "targetId",
      header: "ເປົ້າໝາຍ",
      cell: ({ row }) => (
        <span className="font-mono text-sm">
          {row.original.targetId.slice(0, 8).toUpperCase()}
        </span>
      ),
      size: 120,
    },
    {
      id: "reporter",
      header: "ຜູ້ລາຍງານ",
      cell: ({ row }) =>
        row.original.reporterName ?? row.original.reporterUserId,
      size: 140,
    },
    {
      id: "reason",
      header: "ເຫດຜົນ",
      cell: ({ row }) => (
        <span className="line-clamp-1 text-sm">
          {row.original.reason ?? "—"}
        </span>
      ),
      size: 180,
    },
    createSortableColumn<ReportListItem>("status", "ສະຖານະ", {
      size: 120,
      cell: ({ row }) => {
        const status = row.original.status as keyof typeof STATUS_OPTIONS;
        const opt = STATUS_OPTIONS[status] ?? {
          label: status,
          variant: "secondary" as const,
        };
        return <Badge variant={opt.variant}>{opt.label}</Badge>;
      },
    }),
    createSortableColumn<ReportListItem>("createdAt", "ວັນທີ", {
      size: 140,
      cell: ({ row }) => formatDateLocal(row.original.createdAt),
    }),
    {
      id: "actions",
      header: "ການດຳເນີນການ",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງລາຍລະອຽດ",
              icon: <EyeIcon className="h-4 w-4" />,
              onClick: () => onView(row.original),
            },
          ]}
          maxInline={2}
        />
      ),
      size: 140,
    },
  ];

  return (
    <DataTable<ReportListItem, unknown>
      noDataMessage="ບໍ່ພົບລາຍງານ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (!sorting[0]?.id) return;
        onSortingChange(sorting[0].id as string, !!sorting[0].desc);
      }}
    />
  );
}
